import { useEffect, useId, useRef, useState } from 'react'
import { ENVIRONMENT_PRESETS, type EnvironmentPreset, type EnvironmentSettings } from '@v2/contracts'
import { Icon } from './icons'

interface ScenePickerProps {
  readonly environment: EnvironmentSettings
  readonly reducedMotion: boolean
  readonly disabled: boolean
  readonly onChange: (preset: EnvironmentPreset) => void
  readonly onMotionChange: (enabled: boolean) => void
}

/** A player's scenery preference changes the view without interrupting the game. */
export function ScenePicker({ environment, reducedMotion, disabled, onChange, onMotionChange }: ScenePickerProps) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const groupId = useId()
  const active = ENVIRONMENT_PRESETS.find(preset => preset.id === environment.preset) ?? ENVIRONMENT_PRESETS[0]!

  useEffect(() => {
    if (!open) return
    const dismiss = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false)
    }
    root.current?.querySelector<HTMLInputElement>('input:checked')?.focus()
    document.addEventListener('pointerdown', dismiss, true)
    return () => document.removeEventListener('pointerdown', dismiss, true)
  }, [open])
  useEffect(() => { if (disabled) setOpen(false) }, [disabled])
  const close = () => { setOpen(false); trigger.current?.focus() }

  return <div className="tt-scene-picker" ref={root} onBlur={event => {
    if (event.relatedTarget instanceof Node && !event.currentTarget.contains(event.relatedTarget)) setOpen(false)
  }} onKeyDown={event => { if (open && event.key === 'Escape') { event.stopPropagation(); close() } }}>
    <button ref={trigger} type="button" className={`tt-rail-button${open ? ' is-active' : ''}`} aria-label={`Scene: ${active.label}`} title={`Scene: ${active.label}`} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? groupId : undefined} disabled={disabled} onClick={() => setOpen(current => !current)}>
      <Icon name="sun"/>
    </button>
    {open && <div className="tt-scene-popover" role="dialog" aria-labelledby={`${groupId}-title`} id={groupId}>
      <div className="tt-popover-heading"><h3 id={`${groupId}-title`}>Scene</h3><button type="button" className="tt-icon-button" aria-label="Close scene picker" onClick={close}><Icon name="close" size={15}/></button></div>
      <fieldset className="tt-scene-options">
        <legend className="tt-sr-only">Table scene</legend>
        {ENVIRONMENT_PRESETS.map(preset => <label className={`tt-scene-option${environment.preset === preset.id ? ' is-selected' : ''}`} key={preset.id}>
          <input className="tt-scene-radio" type="radio" name={`${groupId}-preset`} value={preset.id} checked={environment.preset === preset.id} disabled={disabled} onChange={() => onChange(preset.id)}/>
          <span className={`tt-scene-swatch is-${preset.id}`} aria-hidden="true"/>
          <span className="tt-scene-option-name">{preset.label}</span>
        </label>)}
      </fieldset>
      <p className="tt-scene-description" aria-live="polite">{active.description}</p>
      <div className="tt-scene-motion">
        <span id={`${groupId}-motion-label`}>Scenery motion</span>
        <button type="button" className="tt-switch" role="switch" aria-checked={!reducedMotion && environment.motion !== false} aria-labelledby={`${groupId}-motion-label`} title={reducedMotion ? 'Your device prefers reduced motion' : undefined} disabled={disabled || reducedMotion} onClick={() => onMotionChange(environment.motion === false)}><span/></button>
      </div>
    </div>}
  </div>
}
